import { ViewWidget, Widget } from '@nordbox/core';

// widgets/asset-viewer/AssetViewerDockSelect.ts
// 2026-09-25, B1 форма кандидата: выбор файла для постоянной панели в колонке справа.
// [data-asset-viewer-select="<assetUI>"] внутри раскладки [data-av-layout] показывает файл
// в её колонке [data-av-dock]: первый выбор создаёт assetViewerView в колонке, следующие
// меняют файл через showAsset без перезагрузки view. Пока файл не выбран, колонка пустая
// и свёрнута. После перезагрузки списка выбранный файл сохраняется, если остался; иначе
// выбирается первый; пустой список закрывает панель. Вне раскладки с колонкой выбор
// обрабатывает AssetViewerTriggers как data-asset (самостоятельный Lightbox).
// Значение row — asset берётся из data-id строки таблицы: dataset колонки таблицы платформа
// вычисляет по source view, а не по строке, поэтому UI строки в него не подставить.
// 2026-09-25: выбор, который панель не смогла показать, откатывается на показанный файл;
// панель, которая не загрузилась, убирается, и следующий выбор создаёт её заново.
const _SELECT = '[data-asset-viewer-select]';
const _docks = new WeakMap();
let _viewSequence = 0;
/** Asset элемента выбора: явный assetUI либо, для значения row, data-id строки таблицы */
function readSelectAsset(el) {
    const value = el.dataset.assetViewerSelect?.trim();
    if (value !== 'row')
        return value || undefined;
    return el.closest('tr[data-id]')?.dataset.id?.trim() || undefined;
}
/** Раскладка с колонкой, которой принадлежит элемент выбора; null — колонки нет */
function findSelectLayout(opener) {
    const layout = opener.closest('[data-av-layout]');
    const dock = layout?.querySelector(':scope > [data-av-dock]');
    return dock && !dock.contains(opener) ? layout : null;
}
/** Показывает asset в колонке раскладки: создаёт панель при первом выборе */
async function selectInDock(layout, assetUI) {
    _markSelected(layout, assetUI);
    const state = _docks.get(layout) ?? _createPanel(layout, assetUI);
    state.assetUI = assetUI;
    const viewer = await state.viewer;
    if (!viewer || state.assetUI !== assetUI || _docks.get(layout) !== state)
        return;
    if (viewer.file.assetUI === assetUI)
        return;
    try {
        await viewer.showAsset(assetUI);
    }
    catch (error) {
        console.warn('[AssetViewer] Не удалось показать выбранный файл', error);
        // Отметка не остаётся на файле, которого панель не показывает; более новый выбор не трогаем
        if (state.assetUI === assetUI && _docks.get(layout) === state) {
            state.assetUI = viewer.file.assetUI;
            _markSelected(layout, state.assetUI);
        }
    }
}
/** Закрывает панель колонки: уничтожает view, колонка снова свёрнута */
function closeDock(layout) {
    const state = _docks.get(layout);
    if (!state)
        return;
    _docks.delete(layout);
    state.observer.disconnect();
    const root = state.dock.querySelector('.im-asset-viewer');
    if (root)
        Widget.getOnByName(root, 'AssetViewer')?.destroy();
    state.view.destroy();
    state.dock.replaceChildren();
    _markSelected(layout, null);
}
function _createPanel(layout, assetUI) {
    const dock = layout.querySelector(':scope > [data-av-dock]');
    const view = ViewWidget.createInside(dock, {
        dataKey: `assetViewerDock${++_viewSequence}`,
        viewKey: 'storage.vsmodule_web.assetViewerView',
        values: { assetUI },
        deferredLoad: false
    });
    const observer = new MutationObserver(records => {
        // Изменения самой панели список файлов не затрагивают
        if (records.every(record => dock.contains(record.target)))
            return;
        _queueSync(layout);
    });
    observer.observe(layout, { childList: true, subtree: true });
    const state = { dock, assetUI, view, observer, syncQueued: false, viewer: Promise.resolve(null) };
    _docks.set(layout, state);
    state.viewer = _loadViewer(layout, state);
    return state;
}
/**
 * Загружает view панели штатным транспортом и поднимает виджет. Панель, закрытая во время
 * загрузки, виджет не получает. Если панель не поднялась (ошибка модуля или загрузки view),
 * состояние колонки убирается: следующий выбор создаёт панель заново.
 */
async function _loadViewer(layout, state) {
    const current = () => _docks.get(layout) === state;
    let viewer = null;
    try {
        const { AssetViewerWidget } = await import('./AssetViewerWidget.js');
        if (current()) {
            viewer = await new Promise(resolve => {
                state.view.reload(undefined, () => {
                    const root = current() ? state.view.element.querySelector('.im-asset-viewer') : null;
                    resolve(root ? AssetViewerWidget.getOrInit(root) : null);
                }, () => resolve(null));
            });
        }
    }
    catch (error) {
        console.warn('[AssetViewer] Не удалось загрузить панель просмотра', error);
    }
    if (!viewer && current())
        closeDock(layout);
    return current() ? viewer : null;
}
/** Список файлов перерисован (перезагрузка таблицы): сверяем выбранный файл */
function _queueSync(layout) {
    const state = _docks.get(layout);
    if (!state || state.syncQueued)
        return;
    state.syncQueued = true;
    queueMicrotask(() => {
        state.syncQueued = false;
        if (_docks.get(layout) !== state)
            return;
        if (!layout.isConnected) {
            closeDock(layout);
            return;
        }
        const options = _options(layout);
        if (options.length === 0) {
            closeDock(layout);
        }
        else if (!options.some(el => readSelectAsset(el) === state.assetUI)) {
            void selectInDock(layout, readSelectAsset(options[0]));
        }
        else {
            _markSelected(layout, state.assetUI);
        }
    });
}
/** Элементы выбора списка, без самой панели */
function _options(layout) {
    const dock = layout.querySelector(':scope > [data-av-dock]');
    return Array.from(layout.querySelectorAll(_SELECT))
        .filter(el => !dock?.contains(el) && !!readSelectAsset(el));
}
/** Выбранный файл отмечается aria-current; оформление — штатное */
function _markSelected(layout, assetUI) {
    _options(layout).forEach(el => {
        if (assetUI && readSelectAsset(el) === assetUI) {
            if (el.getAttribute('aria-current') !== 'true')
                el.setAttribute('aria-current', 'true');
        }
        else if (el.hasAttribute('aria-current')) {
            el.removeAttribute('aria-current');
        }
    });
}

export { closeDock, findSelectLayout, readSelectAsset, selectInDock };
